import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import {
  Control,
  FieldErrors,
  UseFormRegister,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { useConfirmDialog } from '~/components/DialogsProvider';
import { fetchEmployee } from '~/data/employee';
import { useEmployee } from '~/data/employee/useEmployee';
import { useAllEnums } from '~/data/enums';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { AppDispatch } from '~/store';
import { isValidPhoneNumber } from '~/utils/helper';

import {
  Country,
  EmpOtherContactType,
  deleteEmpOtherContactData,
  putEmpOtherContactData,
} from './data';

export type IFormInput = {
  fEmpCity: string;
  fEmpCountry: string;
  fEmpEmailOther: string;
  fEmpPhoneOther: string | undefined;
  fEmpPostalCode: string;
  fEmpState: string;
  fEmpStreetAddress1: string;
  fEmpStreetAddress2: string;
};

export type useOtherContractOutput = {
  deleteContact: (e: React.MouseEvent<HTMLButtonElement>) => void;
  readMode: boolean;
  setReadMode: (data: boolean) => void;
  empData: EmpOtherContactType;
  onSubmit: (e: React.MouseEvent<HTMLButtonElement>) => void;
  register: UseFormRegister<IFormInput>;
  errors: FieldErrors<IFormInput>;
  countriesList: Country[];
  control: Control<IFormInput>;
  handleFormReset: (totalReset: boolean) => void;
  isLoading: boolean;
};

export const useOtherContract = (): useOtherContractOutput => {
  const dispatch: AppDispatch = useDispatch();
  const confirm = useConfirmDialog();
  const { t } = useTranslation();
  const {
    employee: { empOtherContact: empData, empBasicData },
    isLoading,
  } = useEmployee();

  const { allEnums } = useAllEnums();
  const countriesList = allEnums['COUNTRY'];
  const [readMode, setReadMode] = useState(true);

  const validationSchema = useMemo(() => {
    return yup.object().shape<IFormInput>({
      fEmpPhoneOther: yup
        .string()
        .test('phoneNumberValidation', t('phoneInvalid'), isValidPhoneNumber),
      fEmpEmailOther: yup.string(),
      fEmpStreetAddress1: yup.string().required(t('laFillManFields')),
      fEmpStreetAddress2: yup.string(),
      fEmpPostalCode: yup.string().required(t('laFillManFields')),
      fEmpState: yup.string(),
      fEmpCity: yup.string().required(t('laFillManFields')),
      fEmpCountry: yup.string().required(t('laFillManFields')),
    });
  }, [t]);
  const resolver = useYupValidationResolver(validationSchema);
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    reset,
  } = useForm<IFormInput>({
    resolver,
  });

  const handleFormReset = (totalReset: boolean) => {
    if (totalReset) {
      reset({
        fEmpCity: '',
        fEmpCountry: '',
        fEmpEmailOther: '',
        fEmpPhoneOther: '',
        fEmpPostalCode: '',
        fEmpState: '',
        fEmpStreetAddress1: '',
        fEmpStreetAddress2: '',
      });
    } else {
      reset();
    }
    setReadMode(true);
  };

  const handleFormSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const json: IFormInput = {
        fEmpPhoneOther: data.fEmpPhoneOther,
        fEmpEmailOther: data.fEmpEmailOther,
        fEmpStreetAddress1: data.fEmpStreetAddress1,
        fEmpStreetAddress2: data.fEmpStreetAddress2,
        fEmpPostalCode: data.fEmpPostalCode,
        fEmpState: data.fEmpState,
        fEmpCity: data.fEmpCity,
        fEmpCountry: data.fEmpCountry,
      };
      (async () => {
        try {
          await putEmpOtherContactData(json, empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
          setReadMode(true);
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      })();
    });
  }, [dispatch, empBasicData.fEmpId, enqueueSnackbar, handleSubmit, t]);

  const deleteContact = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    confirm(t('contactDeleteConfirmation'), async (confirmed) => {
      if (confirmed) {
        try {
          await deleteEmpOtherContactData(empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
          handleFormReset(true);
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      }
    });
  };

  const onSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    handleFormSubmit();
  };

  return {
    control,
    deleteContact,
    countriesList,
    readMode,
    setReadMode,
    empData,
    errors,
    register,
    onSubmit,
    handleFormReset,
    isLoading,
  };
};
